import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Rating,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  TwoWheeler,
  DirectionsCar,
  PedalBike,
  DirectionsWalk,
} from '@mui/icons-material';
import driversService, { Driver } from '../../../services/drivers.service';
import AvaliacoesDialog from '../../PagaliFood/AvaliacoesDialog';
import NovoEstafetaDialog from './NovoEstafetaDialog';
import { PersonAdd } from '@mui/icons-material';

/**
 * Os documentos de identificação são privados no Storage: pedem-se à API com a
 * sessão do administrador e mostram-se a partir de um blob local.
 */
function DriverDoc({ id, kind, label }: { id: number; kind: 'bi' | 'carta'; label: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [pdf, setPdf] = useState(false);
  const [erro, setErro] = useState(false);
  useEffect(() => {
    let local: string | null = null;
    driversService.getDoc(id, kind)
      .then((r) => {
        const blob = r.data as Blob;
        setPdf(blob.type === 'application/pdf');
        local = URL.createObjectURL(blob);
        setUrl(local);
      })
      .catch(() => setErro(true));
    return () => { if (local) URL.revokeObjectURL(local); };
  }, [id, kind]);
  return (
    <Paper sx={{ p: 1 }}>
      <Typography variant="caption">{label}</Typography>
      {erro ? (
        <Typography variant="body2" color="error">Não foi possível abrir</Typography>
      ) : !url ? (
        <Typography variant="body2" color="text.secondary">A carregar…</Typography>
      ) : pdf ? (
        <Button href={url} target="_blank">Ver PDF</Button>
      ) : (
        <Box component="a" href={url} target="_blank" rel="noreferrer">
          <Box component="img" src={url} sx={{ maxHeight: 200, display: 'block' }} />
        </Box>
      )}
    </Paper>
  );
}

const vehicleIcons: Record<string, React.ReactNode> = {
  MOTO: <TwoWheeler fontSize="small" />,
  CARRO: <DirectionsCar fontSize="small" />,
  BICICLETA: <PedalBike fontSize="small" />,
  A_PE: <DirectionsWalk fontSize="small" />,
};

const approvedConfig: Record<string, { label: string; color: 'warning' | 'success' | 'error' }> = {
  P: { label: 'Pendente', color: 'warning' },
  A: { label: 'Aprovado', color: 'success' },
  R: { label: 'Rejeitado', color: 'error' },
};

const DriversPage: React.FC = () => {
  const [tab, setTab] = useState<'P' | 'A' | 'R'>('P');
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Driver | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [avaliacoes, setAvaliacoes] = useState<Driver | null>(null);
  const [novo, setNovo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await driversService.list(tab);
      setDrivers(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (driver: Driver) => {
    setActing(true);
    try {
      await driversService.approve(driver.id);
      setSnack('Driver aprovado!');
      setSelected(null);
      await load();
    } catch (e: any) {
      setSnack(e.response?.data?.error || 'Erro ao aprovar');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!selected || !rejectReason.trim()) return;
    setActing(true);
    try {
      await driversService.reject(selected.id, rejectReason);
      setSnack('Driver rejeitado');
      setSelected(null);
      setRejectOpen(false);
      setRejectReason('');
      await load();
    } catch (e: any) {
      setSnack(e.response?.data?.error || 'Erro ao rejeitar');
    } finally {
      setActing(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" mb={1}>
            Pagali Drivers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registar estafetas, e aprovar ou rejeitar candidaturas
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAdd />} onClick={() => setNovo(true)}>
          Novo estafeta
        </Button>
      </Box>

      <NovoEstafetaDialog open={novo} onClose={() => setNovo(false)}
        onCriado={(msg) => { setSnack(msg); if (tab === 'A') load(); else setTab('A'); }} />

      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="P" label="Pendentes" />
          <Tab value="A" label="Aprovados" />
          <Tab value="R" label="Rejeitados" />
        </Tabs>

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : drivers.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>Sem entregadores nesta categoria</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Driver</TableCell>
                  <TableCell>Contacto</TableCell>
                  <TableCell>Veiculo</TableCell>
                  <TableCell>BI / Carta</TableCell>
                  <TableCell>Registo</TableCell>
                  <TableCell>Avaliação</TableCell>
                  <TableCell>Contrato</TableCell>
                  <TableCell align="right">Acoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drivers.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar src={d.avatarUrl}>{d.name?.[0]}</Avatar>
                        <Box>
                          <Typography fontWeight={600}>{d.name}</Typography>
                          <Chip
                            size="small"
                            label={approvedConfig[d.approved]?.label}
                            color={approvedConfig[d.approved]?.color}
                          />
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{d.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {d.phone}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {vehicleIcons[d.vehicleType]}
                        <Box>
                          <Typography variant="body2">{d.vehicleType}</Typography>
                          {d.vehiclePlate && (
                            <Typography variant="caption" color="text.secondary">
                              {d.vehiclePlate}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">BI:</Typography>
                      <Typography variant="body2">{d.biNumero || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">Carta:</Typography>
                      <Typography variant="body2">{d.cartaConducao || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {d.createdAt ? new Date(d.createdAt).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {d.ratingCount ? (
                        <Tooltip title="Ver avaliações">
                          <Link component="button" underline="hover" onClick={() => setAvaliacoes(d)}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <Rating value={Number(d.rating)} precision={0.1} readOnly size="small" />
                            {Number(d.rating).toFixed(1)} ({d.ratingCount})
                          </Link>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">Sem avaliações</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {d.termosVersao ? (
                        <Tooltip title={`Versão ${d.termosVersao}`}>
                          <Chip size="small" color="success" variant="outlined"
                            label={`Aceite ${d.termosAceitesEm ? new Date(d.termosAceitesEm).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : ''}`} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Aceita no primeiro acesso ao Pagali Driver (site; na app a partir da próxima versão)">
                          <Chip size="small" color="warning" variant="outlined" label="Por aceitar" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver documentos">
                        <IconButton onClick={() => setSelected(d)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {d.approved === 'P' && (
                        <>
                          <Tooltip title="Aprovar">
                            <IconButton color="success" onClick={() => handleApprove(d)} disabled={acting}>
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rejeitar">
                            <IconButton color="error" onClick={() => { setSelected(d); setRejectOpen(true); }} disabled={acting}>
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog de detalhes */}
      <Dialog open={!!selected && !rejectOpen} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Driver: {selected?.name}
          <Typography variant="body2" color="text.secondary">{selected?.email}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" mb={1}>Informacoes</Typography>
                <Stack direction="row" spacing={3} flexWrap="wrap">
                  <Box><Typography variant="caption">BI</Typography><Typography>{selected.biNumero || '-'}</Typography></Box>
                  <Box><Typography variant="caption">Carta</Typography><Typography>{selected.cartaConducao || '-'}</Typography></Box>
                  <Box><Typography variant="caption">Veiculo</Typography><Typography>{selected.vehicleType}</Typography></Box>
                  {selected.vehiclePlate && <Box><Typography variant="caption">Matricula</Typography><Typography>{selected.vehiclePlate}</Typography></Box>}
                  {selected.vehicleModel && <Box><Typography variant="caption">Modelo</Typography><Typography>{selected.vehicleModel}</Typography></Box>}
                </Stack>
              </Box>

              {selected.rejectionReason && (
                <Alert severity="error">
                  <Typography variant="subtitle2">Motivo da rejeicao</Typography>
                  {selected.rejectionReason}
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle2" mb={2}>Documentos</Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {selected.biDocUrl && <DriverDoc id={selected.id} kind="bi" label="BI" />}
                  {selected.cartaDocUrl && <DriverDoc id={selected.id} kind="carta" label="Carta" />}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Fechar</Button>
          {selected?.approved === 'P' && (
            <>
              <Button color="error" onClick={() => setRejectOpen(true)}>Rejeitar</Button>
              <Button variant="contained" color="success" onClick={() => handleApprove(selected)} disabled={acting}>
                Aprovar
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog de rejeicao */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rejeitar entregador</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Motivo da rejeicao"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleReject} disabled={acting || !rejectReason.trim()}>
            Rejeitar
          </Button>
        </DialogActions>
      </Dialog>

      {snack && (
        <Alert
          severity="info"
          onClose={() => setSnack(null)}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1500 }}
        >
          {snack}
        </Alert>
      )}
      <AvaliacoesDialog open={!!avaliacoes} onClose={() => setAvaliacoes(null)}
        titulo={avaliacoes?.name || ''} filtro={{ driverId: avaliacoes?.userId }} />
    </Box>
  );
};

export default DriversPage;
